/**
 * Truth v2 utilisation client — zeecore /utilisation/v2/*
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import type {
  TruthUtilisationJobRecord,
  TruthUtilisationStatusResponse,
  TruthUtilisationVehicleDayRow,
  UtilisationAssumptionsConfig,
} from '../types/utilisationTruth';

export type {
  TruthUtilisationVehicleDayRow,
  TruthUtilisationStatusResponse,
  TruthUtilisationJobRecord,
  UtilisationAssumptionsConfig,
  UtilisationAnalysisConfig,
} from '../types/utilisationTruth';

export async function fetchTruthUtilisationVehicleDays(
  startDate: string,
  endDate: string,
  options?: { cacheBust?: number; signal?: AbortSignal },
): Promise<TruthUtilisationVehicleDayRow[]> {
  let url = API_ENDPOINTS.utilisationV2.vehicleDays(startDate, endDate);
  if (options?.cacheBust != null) {
    url += `${url.includes('?') ? '&' : '?'}_=${options.cacheBust}`;
  }
  const response = await authenticatedFetch(url, {
    cache: 'no-store',
    signal: options?.signal,
  });
  if (!response.ok) {
    const text = await response.text();
    if (text.trimStart().startsWith('<')) {
      throw new Error(
        `Truth utilisation API returned HTML (${response.status}). Restart zeecore and ensure /utilisation/v2 is proxied (Vite dev) or set VITE_ZEECORE_BASE_URL.`,
      );
    }
    throw new Error(`Truth utilisation vehicle-days failed: ${response.status} ${text.slice(0, 200)}`);
  }
  const json = await response.json();
  return json.records || [];
}

export async function fetchTruthUtilisationStatus(
  startDate: string,
  endDate: string,
  options?: { signal?: AbortSignal },
): Promise<TruthUtilisationStatusResponse> {
  const url = API_ENDPOINTS.utilisationV2.status(startDate, endDate);
  const response = await authenticatedFetch(url, { signal: options?.signal, cache: 'no-store' });
  if (!response.ok) {
    const text = await response.text();
    if (text.trimStart().startsWith('<')) {
      throw new Error(
        `Truth utilisation status returned HTML (${response.status}). Restart zeecore and ensure /utilisation/v2 is proxied.`,
      );
    }
    throw new Error(`Truth utilisation status failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return await response.json();
}

export async function fetchTruthUtilisationJobs(): Promise<TruthUtilisationJobRecord[]> {
  const response = await authenticatedFetch(API_ENDPOINTS.utilisationV2.jobsList());
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data.jobs) ? data.jobs : [];
}

export async function triggerTruthUtilisationCompute(
  startDate: string,
  endDate: string,
): Promise<void> {
  const response = await authenticatedFetch(API_ENDPOINTS.utilisationV2.compute(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ startDate, endDate }),
  });
  if (!response.ok) {
    const text = await response.text();
    if (text.trimStart().startsWith('<')) {
      throw new Error(`Utilisation compute API not found (${response.status}). Restart zeecore after pulling latest code.`);
    }
    try {
      const err = JSON.parse(text) as { message?: string; error?: string };
      throw new Error(err.message ?? err.error ?? `Utilisation compute failed (${response.status})`);
    } catch (parseErr) {
      if (parseErr instanceof Error && parseErr.message.startsWith('Utilisation')) throw parseErr;
      throw new Error(`Utilisation compute failed (${response.status})`);
    }
  }
}

export function utilisationLastComputedMeta(
  last: TruthUtilisationStatusResponse['last_computed'],
): { computedAt: string | null; recordCount: number | null } {
  if (!last) return { computedAt: null, recordCount: null };
  return {
    computedAt: last.computed_at ?? null,
    recordCount: last.record_count ?? null,
  };
}

/** Load declared utilisation targets (zeecore canonical config). */
export async function fetchTruthUtilisationConfig(): Promise<UtilisationAssumptionsConfig | null> {
  const response = await authenticatedFetch(API_ENDPOINTS.utilisationV2.config());
  if (response.status === 404) return null;
  if (!response.ok) {
    const text = await response.text();
    if (text.trimStart().startsWith('<')) {
      throw new Error(
        `Truth utilisation config returned HTML (${response.status}). Restart zeecore and ensure /utilisation/v2 is proxied.`,
      );
    }
    throw new Error(`Truth utilisation config failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as UtilisationAssumptionsConfig;
}

/** Save utilisation assumptions; returns persisted config from zeecore. */
export async function saveTruthUtilisationConfig(
  config: UtilisationAssumptionsConfig,
): Promise<UtilisationAssumptionsConfig> {
  const response = await authenticatedFetch(API_ENDPOINTS.utilisationV2.config(), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Save utilisation config failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as UtilisationAssumptionsConfig;
}
